import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Minus, Plus, Star, Truck, ShieldCheck, Leaf, ChevronDown } from "lucide-react";
import api, { img, money } from "../lib/api";
import { useStore } from "../context/StoreContext";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

function Accordion({ title, children, open: initOpen = false, testid }) {
  const [open, setOpen] = useState(initOpen);
  return (
    <div className="border-b border-sage/30">
      <button onClick={() => setOpen(!open)} data-testid={testid} className="w-full flex items-center justify-between py-5 text-left">
        <span className="serif text-xl">{title}</span>
        <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-6 text-forest/70 leading-relaxed text-sm space-y-2">{children}</div>}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addToCart, wishlist, toggleWishlist, user } = useStore();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState({ rating: 5, title: "", body: "", name: "" });

  useEffect(() => {
    window.scrollTo(0, 0);
    setP(null); setActive(0); setQty(1);
    api.get(`/products/${slug}`).then(r => setP(r.data)).catch(() => nav("/shop"));
    api.get(`/products/${slug}/reviews`).then(r => setReviews(r.data));
    if (localStorage.getItem("iah_token")) api.post(`/account/recently-viewed/${slug}`).catch(() => {});
  }, [slug, nav]);

  if (!p) return <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 grid lg:grid-cols-2 gap-12"><div className="aspect-[4/5] shimmer" /><div className="space-y-4"><div className="h-8 w-2/3 shimmer" /><div className="h-4 w-full shimmer" /><div className="h-4 w-1/2 shimmer" /></div></div>;

  const imgs = (p.images || []).map(img);
  const discount = p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  const wished = wishlist.includes(p.slug);
  const inStock = (p.inventory?.available || 0) > 0 || p.backorder;

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/reviews", { product_slug: p.slug, ...form });
      toast.success(r.data.message);
      setForm({ rating: 5, title: "", body: "", name: "" });
    } catch { toast.error("Could not submit review"); }
  };

  return (
    <div>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-8 lg:py-14">
        <div className="text-xs text-forest/45 mb-8"><Link to="/shop" className="hover:text-forest">Shop</Link> / <Link to={`/collections/${p.category}`} className="hover:text-forest capitalize">{p.category}</Link> / {p.name}</div>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* GALLERY */}
          <div>
            <div className="overflow-hidden bg-beige/40 aspect-[4/5] mb-3">
              <img src={imgs[active]} alt={p.name} className="h-full w-full object-cover" />
            </div>
            <div className="flex gap-3 overflow-x-auto hide-scrollbar">
              {imgs.map((src, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`}
                  className={`h-20 w-16 flex-shrink-0 overflow-hidden bg-beige/40 border ${active === i ? "border-forest" : "border-transparent"}`}>
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* INFO */}
          <div>
            <span className="eyebrow text-moss">{p.subtitle}</span>
            <h1 className="serif text-4xl lg:text-5xl mt-2 mb-3">{p.name}</h1>
            {p.review_count > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className={i < Math.round(p.rating) ? "fill-forest text-forest" : "text-forest/25"} />)}</div>
                <span className="text-xs text-forest/60">{p.rating} · {p.review_count} reviews</span>
              </div>
            )}
            <p className="text-forest/70 leading-relaxed mb-6">{p.short}</p>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="serif text-3xl">{money(p.price)}</span>
              {discount > 0 && <><span className="text-forest/40 line-through">{money(p.mrp)}</span><span className="text-xs bg-brown text-ivory px-2 py-1 uppercase tracking-wide">Save {discount}%</span></>}
            </div>
            <div className="flex flex-wrap gap-2 mb-6">
              {p.size && <span className="border border-forest text-forest px-4 py-2 text-xs uppercase tracking-wide">{p.size}</span>}
            </div>

            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center border border-forest/25">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3.5" data-testid="qty-dec"><Minus size={14} /></button>
                <span className="px-4 text-sm" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-4 py-3.5" data-testid="qty-inc"><Plus size={14} /></button>
              </div>
              <button onClick={() => toggleWishlist(p.slug)} data-testid="pdp-wishlist" className="h-[50px] w-[50px] flex items-center justify-center border border-forest/25 hover:bg-beige/50 transition-colors">
                <Heart size={18} className={wished ? "fill-forest text-forest" : "text-forest"} strokeWidth={1.4} />
              </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <button disabled={!inStock} onClick={() => addToCart(p, qty)} data-testid="pdp-add-cart" className="btn-outline flex-1 disabled:opacity-40">{inStock ? "Add to Cart" : "Out of Stock"}</button>
              <button disabled={!inStock} onClick={() => { addToCart(p, qty); nav("/checkout"); }} data-testid="pdp-buy-now" className="btn-primary flex-1 disabled:opacity-40">Buy Now</button>
            </div>

            <div className="grid grid-cols-3 gap-4 py-6 border-y border-sage/30 mb-2 text-center">
              <div className="flex flex-col items-center gap-2"><Truck size={20} strokeWidth={1.3} /><span className="text-[11px] text-forest/60 leading-tight">Free shipping over ₹999</span></div>
              <div className="flex flex-col items-center gap-2"><Leaf size={20} strokeWidth={1.3} /><span className="text-[11px] text-forest/60 leading-tight">Nature inspired</span></div>
              <div className="flex flex-col items-center gap-2"><ShieldCheck size={20} strokeWidth={1.3} /><span className="text-[11px] text-forest/60 leading-tight">Cruelty free</span></div>
            </div>

            {/* Certifications */}
            <div className="py-6 border-b border-sage/30 mb-2" data-testid="product-certifications">
              <div className="eyebrow text-moss mb-4">Certifications</div>
              <div className="grid grid-cols-6 gap-2 sm:gap-3 max-w-md">
                {[
                  { src: "/certs/sulfate-free.png", label: "Sulfate Free" },
                  { src: "/certs/iso-9001.png", label: "ISO 9001:2008" },
                  { src: "/certs/llp-free.png", label: "LLP Free" },
                  { src: "/certs/who-gmp.png", label: "WHO GMP Certified" },
                  { src: "/certs/fda.png", label: "FDA Approved" },
                  { src: "/certs/cruelty-free.png", label: "Not Tested on Animals" },
                ].map((c) => (
                  <div key={c.label} className="flex flex-col items-center text-center" data-testid={`cert-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <img src={c.src} alt={c.label} className="w-10 h-10 sm:w-12 sm:h-12 object-contain" loading="lazy" />
                    <span className="text-[9px] sm:text-[10px] text-forest/60 mt-2 leading-tight tracking-wide">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {p.bundle_items?.length > 0 && (
              <div className="mb-8 border border-sage/40 bg-beige/30 rounded-lg p-5">
                <div className="eyebrow text-moss mb-4">What's Included</div>
                <div className="space-y-3">
                  {p.bundle_items.map((m) => (
                    <Link key={m.slug} to={`/product/${m.slug}`} className="flex items-center gap-3 group/item" data-testid={`bundle-item-${m.slug}`}>
                      <img src={img(m.image)} alt={m.name} className="h-14 w-12 object-cover bg-ivory rounded" />
                      <span className="serif text-lg group-hover/item:opacity-70 transition-opacity flex-1">{m.name}</span>
                      <span className="text-sm text-forest/50">{money(m.price)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Accordions */}
            <div className="mt-4">
              <Accordion title="Key Benefits" open testid="acc-benefits">
                <ul className="list-disc pl-5 space-y-1">{p.benefits?.map((b, i) => <li key={i}>{b}</li>)}</ul>
              </Accordion>
              <Accordion title="Ingredients" testid="acc-ingredients">
                {p.key_actives?.length > 0 && <p className="mb-2"><strong className="text-forest">Key Actives:</strong> {p.key_actives.join(", ")}</p>}
                <p>{p.ingredients}</p>
              </Accordion>
              <Accordion title="How to Use" testid="acc-howto">
                <ol className="list-decimal pl-5 space-y-1">{p.how_to_use?.map((h, i) => <li key={i}>{h}</li>)}</ol>
              </Accordion>
              <Accordion title="Suitable For & Texture" testid="acc-suitable">
                <p><strong className="text-forest">Suitable for:</strong> {p.suitable_for}</p>
                <p><strong className="text-forest">Texture:</strong> {p.texture}</p>
              </Accordion>
              <Accordion title="Product Story & Meghalaya Inspiration" testid="acc-story">
                <p className="mb-2">{p.story}</p>
                <p className="text-forest/50 italic">{p.meghalaya_inspiration}</p>
              </Accordion>
              {p.faqs?.length > 0 && (
                <Accordion title="FAQs" testid="acc-faqs">
                  {p.faqs.map((f, i) => <div key={i} className="mb-3"><p className="text-forest font-medium">{f.q}</p><p>{f.a}</p></div>)}
                </Accordion>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 border-t border-sage/30">
        <h2 className="serif text-4xl mb-10">Customer Reviews</h2>
        <div className="grid lg:grid-cols-[1fr_360px] gap-12">
          <div className="space-y-8">
            {reviews.length === 0 && <p className="text-forest/50">Be the first to share your ritual.</p>}
            {reviews.map((r) => (
              <div key={r.id} className="border-b border-sage/20 pb-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={13} className={i < r.rating ? "fill-forest text-forest" : "text-forest/20"} />)}</div>
                  <span className="text-xs text-forest/50">{r.name} {r.verified && "· Verified Purchase"}</span>
                </div>
                <p className="serif text-xl mb-1">{r.title}</p>
                <p className="text-sm text-forest/70">{r.body}</p>
              </div>
            ))}
          </div>
          <div className="bg-beige/40 p-8 h-fit">
            <h3 className="serif text-2xl mb-5">Write a Review</h3>
            <form onSubmit={submitReview} className="space-y-4">
              <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}>
                  <Star size={22} className={n <= form.rating ? "fill-forest text-forest" : "text-forest/25"} />
                </button>))}
              </div>
              <input className="input-line" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name" />
              <input className="input-line" placeholder="Review title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title" />
              <textarea className="input-line resize-none" rows={4} placeholder="Share your experience…" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="review-body" />
              <button className="btn-primary w-full" data-testid="review-submit">Submit Review</button>
            </form>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {p.related_products?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16">
          <Reveal className="mb-10"><span className="eyebrow text-moss">You May Also Like</span><h2 className="serif text-4xl mt-3">Related Products</h2></Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
            {p.related_products.map((rp, i) => <ProductCard key={rp.slug} product={rp} index={i} />)}
          </div>
        </section>
      )}

      {/* sticky mobile add */}
      <div className="lg:hidden fixed bottom-[57px] left-0 right-0 z-30 bg-ivory/95 backdrop-blur border-t border-sage/30 p-3 flex gap-3">
        <div className="flex flex-col justify-center px-2"><span className="serif text-xl leading-none">{money(p.price)}</span></div>
        <button disabled={!inStock} onClick={() => addToCart(p, qty)} data-testid="sticky-add-cart" className="btn-primary flex-1 py-3">{inStock ? "Add to Cart" : "Out of Stock"}</button>
      </div>
    </div>
  );
}
